/**
 * units.js — Unit conversion and formatting utilities
 * 
 * Supports: mm, cm, m, inch, ft, sqft (area)
 * All internal storage is in mm and mm² (metric SI)
 */

export const UNIT_SYSTEMS = {
  METRIC: "metric",
  IMPERIAL: "imperial",
  MIXED: "mixed",
};

export const DISTANCE_UNITS = {
  MM: { key: "mm", label: "mm", toMm: v => v, fromMm: v => v, precision: 0 },
  CM: { key: "cm", label: "cm", toMm: v => v * 10, fromMm: v => v / 10, precision: 1 },
  M:  { key: "m",  label: "m",  toMm: v => v * 1000, fromMm: v => v / 1000, precision: 2 },
  IN: { key: "in", label: "″",  toMm: v => v * 25.4, fromMm: v => v / 25.4, precision: 2 },
  FT: { key: "ft", label: "′",  toMm: v => v * 304.8, fromMm: v => v / 304.8, precision: 2 },
};

export const AREA_UNITS = {
  MM2: { key: "mm2", label: "mm²", toMm2: v => v, fromMm2: v => v, precision: 0 },
  CM2: { key: "cm2", label: "cm²", toMm2: v => v * 100, fromMm2: v => v / 100, precision: 1 },
  M2:  { key: "m2",  label: "m²",  toMm2: v => v * 1e6, fromMm2: v => v / 1e6, precision: 2 },
  SQFT: { key: "sqft", label: "sq ft", toMm2: v => v * 92903.04, fromMm2: v => v / 92903.04, precision: 1 },
};

// Standard presets for different locales/regions
export const UNIT_PRESETS = {
  INDIA: {
    name: "India (Metric)",
    system: UNIT_SYSTEMS.METRIC,
    distance: DISTANCE_UNITS.M,
    area: AREA_UNITS.M2,
    symbol: "₹",
  },
  USA: {
    name: "USA (Imperial)",
    system: UNIT_SYSTEMS.IMPERIAL,
    distance: DISTANCE_UNITS.FT,
    area: AREA_UNITS.SQFT,
    symbol: "$",
  },
  UK: {
    name: "UK (Metric/Imperial)",
    system: UNIT_SYSTEMS.MIXED,
    distance: DISTANCE_UNITS.M,
    area: AREA_UNITS.SQFT,
    symbol: "£",
  },
  DEFAULT: {
    name: "Default (Metric)",
    system: UNIT_SYSTEMS.METRIC,
    distance: DISTANCE_UNITS.M,
    area: AREA_UNITS.M2,
    symbol: "₹",
  },
};

/**
 * Format distance in mm to selected unit
 * @param {number} mm - distance in millimeters
 * @param {Object} unit - DISTANCE_UNITS entry
 * @returns {string} formatted distance with unit
 */
export function formatDistance(mm, unit = DISTANCE_UNITS.M) {
  if (mm == null) return "—";
  const value = unit.fromMm(Math.abs(mm));
  const formatted = value.toFixed(unit.precision).replace(/\.?0+$/, "");
  return `${formatted}${unit.label}`;
}

/**
 * Format area in mm² to selected unit
 * @param {number} mm2 - area in square millimeters
 * @param {Object} unit - AREA_UNITS entry
 * @returns {string} formatted area with unit
 */
export function formatArea(mm2, unit = AREA_UNITS.M2) {
  if (mm2 == null) return "—";
  const value = unit.fromMm2(Math.abs(mm2));
  const formatted = value.toFixed(unit.precision).replace(/\.?0+$/, "");
  return `${formatted}${unit.label}`;
}

/**
 * Format distance with intelligent unit selection
 * (e.g., 1500mm → "1.5m", 350mm → "35cm")
 * @param {number} mm - distance in millimeters
 * @param {Object} preset - UNIT_PRESETS entry
 * @returns {string} formatted distance
 */
export function formatDistanceAuto(mm, preset = UNIT_PRESETS.DEFAULT) {
  if (mm == null) return "—";
  const absMm = Math.abs(mm);
  
  if (preset.system === UNIT_SYSTEMS.IMPERIAL) {
    // For imperial: prefer ft for larger distances, inches for smaller
    if (absMm >= 304.8) return formatDistance(mm, DISTANCE_UNITS.FT);
    return formatDistance(mm, DISTANCE_UNITS.IN);
  }
  
  if (preset.system === UNIT_SYSTEMS.METRIC) {
    // For metric: prefer m for larger distances, cm for smaller
    if (absMm >= 1000) return formatDistance(mm, DISTANCE_UNITS.M);
    if (absMm >= 100) return formatDistance(mm, DISTANCE_UNITS.CM);
    return formatDistance(mm, DISTANCE_UNITS.MM);
  }
  
  // Mixed: same as metric
  if (absMm >= 1000) return formatDistance(mm, DISTANCE_UNITS.M);
  if (absMm >= 100) return formatDistance(mm, DISTANCE_UNITS.CM);
  return formatDistance(mm, DISTANCE_UNITS.MM);
}

/**
 * Parse user input to mm
 * Handles: "100", "10cm", "1.5m", "5ft", etc
 */
export function parseDistance(input) {
  if (!input) return null;
  const str = String(input).trim().toLowerCase();
  
  // Extract number and unit
  const match = str.match(/^([\d.]+)\s*([a-z″′]+)?$/i);
  if (!match) return null;
  
  const value = parseFloat(match[1]);
  if (isNaN(value)) return null;
  
  const unitStr = (match[2] || "mm").replace(/[″′]/g, "");
  
  // Find matching unit
  for (const unit of Object.values(DISTANCE_UNITS)) {
    if (unit.key === unitStr || unit.label === unitStr) {
      return unit.toMm(value);
    }
  }
  
  return value; // assume mm if no unit
}

/**
 * Get available distance units as array
 */
export function getDistanceUnitsArray() {
  return Object.values(DISTANCE_UNITS);
}

/**
 * Get available area units as array
 */
export function getAreaUnitsArray() {
  return Object.values(AREA_UNITS);
}

/**
 * Get available presets as array
 */
export function getPresetsArray() {
  return Object.values(UNIT_PRESETS);
}
